import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useEnvironment } from '@/features/environments'
import { useTenant } from '@/features/tenants'
import { AppLayout } from '@/shared/components/layout/AppLayout'
import { BreadcrumbsContext } from '@/shared/hooks/useBreadcrumbs'

export const Route = createFileRoute('/_auth')({
  beforeLoad: ({ context, location }) => {
    // AuthProvider blocks rendering until isLoading is false,
    // so by the time beforeLoad runs, auth state is resolved.
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.href,
        },
      })
    }
  },
  component: AuthLayout,
})

function AuthLayout() {
  const [breadcrumbs, setBreadcrumbsState] = useState<React.ReactNode>(null)
  const { isLoadingTenants, currentTenant } = useTenant()
  const {
    isLoadingEnvironments,
    isLoadingCurrentEnvironment,
    currentEnvironment,
  } = useEnvironment()

  const setBreadcrumbs = useCallback((newBreadcrumbs: React.ReactNode) => {
    setBreadcrumbsState(newBreadcrumbs)
  }, [])

  const clearBreadcrumbs = useCallback(() => {
    setBreadcrumbsState(null)
  }, [])

  // Gate: block child routes until tenant + environment are resolved
  const isContextReady =
    !isLoadingTenants &&
    !!currentTenant &&
    !isLoadingEnvironments &&
    !isLoadingCurrentEnvironment &&
    !!currentEnvironment

  if (!isContextReady) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
      </div>
    )
  }

  return (
    <BreadcrumbsContext.Provider value={{ setBreadcrumbs, clearBreadcrumbs }}>
      <AppLayout breadcrumbs={breadcrumbs}>
        <Outlet />
      </AppLayout>
    </BreadcrumbsContext.Provider>
  )
}
